import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  DeleteOutline,
  Inventory as InventoryIcon,
  CheckCircleOutline,
  HighlightOff,
  HourglassTop,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Clear,
  Close as CloseIcon,
  ReceiptLongOutlined,
  TrendingUp,
  WarningAmber,
  ErrorOutline,
  ExpandMore,
  ExpandLess,
  LockOutlined,
  Timeline as TimelineIcon,
  ArrowDownward,
  ArrowUpward,
  PersonOutline,
  LocalPharmacy,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { loadDispensaryProducts, saveRequisitions, getRequisitions, deleteRequisition, updateRequisition, productTrace } from '../BaseURL';

// ── Theme tokens (mirrors Home.js / Procurement) ───────────────────────────
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// Status palette
const STATUS = {
  pending:  { color: '#d97706', tint: 'rgba(217,119,6,0.10)',  strong: '#92400E', label: 'Pending',  Icon: HourglassTop },
  approved: { color: '#10b981', tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'Approved', Icon: CheckCircleOutline },
  rejected: { color: '#dc2626', tint: 'rgba(220,38,38,0.10)',  strong: '#991B1B', label: 'Rejected', Icon: HighlightOff },
};
const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  const Lv = s.Icon;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: s.tint, color: s.strong,
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      <Lv sx={{ fontSize: 12 }} />
      {s.label}
    </Box>
  );
};

// Deterministic colour for a requester avatar
const REQUESTER_COLORS = [
  { bg: 'rgba(65,163,134,0.12)',  fg: '#15803D' },
  { bg: 'rgba(14,165,233,0.12)',  fg: '#0369A1' },
  { bg: 'rgba(139,92,246,0.12)',  fg: '#6D28D9' },
  { bg: 'rgba(217,119,6,0.12)',   fg: '#92400E' },
  { bg: 'rgba(236,72,153,0.12)',  fg: '#BE185D' },
  { bg: 'rgba(20,184,166,0.12)',  fg: '#0F766E' },
];
const colourFor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return REQUESTER_COLORS[h % REQUESTER_COLORS.length];
};
const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

// Stock palette
const STOCK = {
  out: { tint: 'rgba(220,38,38,0.10)', strong: '#991B1B', label: 'Out of stock' },
  low: { tint: 'rgba(217,119,6,0.10)', strong: '#92400E', label: 'Low stock'    },
  in:  { tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'In stock'    },
};

const Requisition = () => {
  // ── Data ────────────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Tab state ───────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ── My Products ────────────────────────────────────────────────────────
  const [productsSearchTerm, setProductsSearchTerm] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [myProductsPage, setMyProductsPage] = useState(0);
  const [myProductsRowsPerPage, setMyProductsRowsPerPage] = useState(10);

  // ── Requisitions list pagination + filters ─────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [requestedByFilter, setRequestedByFilter] = useState('');
  const [approvedByFilter, setApprovedByFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reqSearch, setReqSearch] = useState('');

  // ── New requisition dialog ─────────────────────────────────────────────
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  // Per-row quantity typed inline against each dispensary product.
  // Shape: { [productName]: '12' }. Cleared after the row is added to the cart.
  const [rowQty, setRowQty] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [savingRequisition, setSavingRequisition] = useState(false);

  // ── Requester action (approve/reject self) ─────────────────────────────
  const [requesterActionDialog, setRequesterActionDialog] = useState({
    open: false, requisition: null, action: '', reason: '', processing: false,
  });
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRequisition, setActionMenuRequisition] = useState(null);

  // History grouping — which (requester · date) rows are open.
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // ── Product trace dialog (My Products → Trace button) ──────────────────
  const [traceDialog, setTraceDialog] = useState({
    open: false,
    productName: '',
    loading: false,
    data: null,
    error: null,
    from: '',
    to: '',
    requesterFilter: '',
  });

  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
  const currentUserName = userInfo.name || '';

  // ── Stock helpers ──────────────────────────────────────────────────────
  const getStockStatus = (product) => {
    if (product.currentBalance === 0) return 'out';
    if (product.currentBalance <= product.st_level) return 'low';
    return 'in';
  };

  // ── Loaders ────────────────────────────────────────────────────────────
  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(loadDispensaryProducts);
      if (response.data.success) setProducts(response.data.results);
    } catch (err) {
      console.error('Error loading dispensary products:', err);
      setError('Failed to load dispensary products');
    } finally {
      setLoading(false);
    }
  };

  const loadRequisitions = async () => {
    try {
      const response = await axios.get(getRequisitions);
      if (response.data.success) setRequisitions(response.data.results);
    } catch (err) {
      console.error('Error loading requisitions:', err);
      const saved = JSON.parse(localStorage.getItem('requisitions')) || [];
      setRequisitions(saved);
    }
  };

  useEffect(() => {
    loadProducts();
    loadRequisitions();
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────
  const stockStats = useMemo(() => {
    const stats = { out: 0, low: 0, in: 0, total: products.length };
    products.forEach(p => { stats[getStockStatus(p)]++; });
    return stats;
  }, [products]);

  const filteredMyProducts = useMemo(() => {
    let filtered = products;
    if (stockFilter !== 'all') filtered = filtered.filter(p => getStockStatus(p) === stockFilter);
    if (productsSearchTerm.trim()) {
      const q = productsSearchTerm.toLowerCase();
      filtered = filtered.filter(p => p.name?.toLowerCase().includes(q));
    }
    return filtered;
  }, [products, stockFilter, productsSearchTerm]);

  const filteredRequisitions = useMemo(() => {
    return requisitions.filter(req => {
      if (dateFrom && req.requestDate < dateFrom) return false;
      if (dateTo && req.requestDate > dateTo) return false;
      if (requestedByFilter && req.requestedBy !== requestedByFilter) return false;
      if (approvedByFilter && req.approvedBy !== approvedByFilter) return false;
      if (statusFilter !== 'all' && req.status !== statusFilter) return false;
      if (reqSearch.trim()) {
        const q = reqSearch.toLowerCase();
        if (!(req.productName || '').toLowerCase().includes(q) &&
            !(req.requestedBy || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [requisitions, dateFrom, dateTo, requestedByFilter, approvedByFilter, statusFilter, reqSearch]);

  // Group the visible requisitions by (requester · request date) so a single
  // submission session collapses into one expandable row.
  const groupedRequisitions = useMemo(() => {
    const m = new Map();
    for (const r of filteredRequisitions) {
      const requester = r.requestedBy || 'Unknown';
      const date = r.requestDate || '—';
      const key = `${requester}::${date}`;
      if (!m.has(key)) m.set(key, { key, requester, date, items: [] });
      m.get(key).items.push(r);
    }
    const out = [];
    for (const g of m.values()) {
      const items = g.items;
      const totalUnits = items.reduce((s, x) => s + parseInt(x.requestedQuantity || 0), 0);
      const pending  = items.filter(x => x.status === 'pending').length;
      const approved = items.filter(x => x.status === 'approved').length;
      const rejected = items.filter(x => x.status === 'rejected').length;
      out.push({ ...g, totalUnits, pending, approved, rejected, totalLines: items.length });
    }
    // Newest date first; within a date, requesters with most pending first.
    out.sort((a, b) => {
      const d = String(b.date).localeCompare(String(a.date));
      if (d !== 0) return d;
      return (b.pending - a.pending) || a.requester.localeCompare(b.requester);
    });
    return out;
  }, [filteredRequisitions]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const distinctRequestedBy = useMemo(
    () => [...new Set(requisitions.map(r => r.requestedBy).filter(Boolean))].sort(),
    [requisitions]
  );
  const distinctApprovedBy = useMemo(
    () => [...new Set(requisitions.map(r => r.approvedBy).filter(Boolean))].sort(),
    [requisitions]
  );

  // ── Filtered products for New Requisition picker ───────────────────────
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;
    const q = searchTerm.toLowerCase();
    return products.filter(p => p.name?.toLowerCase().includes(q));
  }, [products, searchTerm]);

  useEffect(() => { setMyProductsPage(0); }, [productsSearchTerm, stockFilter]);
  useEffect(() => {
    setPage(0);
    setExpandedGroups(new Set());
  }, [statusFilter, dateFrom, dateTo, requestedByFilter, approvedByFilter, reqSearch]);

  // ── New Requisition handlers (logic preserved) ─────────────────────────
  const handleOpenDialog = () => {
    setOpenDialog(true);
    setRowQty({});
    setSearchTerm('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProducts([]);
    setRowQty({});
  };

  // Add (or update) a row in the cart from the inline qty field on the
  // dispensary list — keyed by product name so each row tracks its own value.
  const handleAddRow = (product) => {
    const raw = rowQty[product.name];
    const quantity = parseInt(raw);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      Swal.fire({
        title: 'Quantity required',
        text: `Type how many ${product.name} you need first.`,
        icon: 'warning',
        toast: true, position: 'top-end', timer: 2200, showConfirmButton: false,
      });
      return;
    }

    const existingIndex = selectedProducts.findIndex(p => p.name === product.name);
    if (existingIndex >= 0) {
      const updated = [...selectedProducts];
      updated[existingIndex] = { ...updated[existingIndex], requestedQuantity: quantity };
      setSelectedProducts(updated);
    } else {
      setSelectedProducts(prev => [...prev, {
        ...product, requestedQuantity: quantity, remarks: '',
      }]);
    }

    // Clear the typed value for that row so the field is ready for the next entry.
    setRowQty(prev => { const next = { ...prev }; delete next[product.name]; return next; });

    // Lightweight confirmation — fast adds shouldn't be interrupted.
    Swal.fire({
      title: existingIndex >= 0 ? 'Updated' : 'Added',
      text: `${product.name} · ${quantity}`,
      icon: 'success', timer: 900, showConfirmButton: false, toast: true, position: 'top-end',
    });
  };

  const handleRemoveProductFromList = (index) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== index));
  };

  // Inline edit qty in the cart
  const handleCartQtyChange = (index, value) => {
    setSelectedProducts(prev => {
      const next = [...prev];
      next[index] = { ...next[index], requestedQuantity: value };
      return next;
    });
  };

  const handleSubmitRequisition = async () => {
    if (selectedProducts.length === 0) {
      Swal.fire({ title: 'Empty list', text: 'Add at least one product.', icon: 'error' });
      return;
    }

    // Validate every line has a positive quantity before we ever ask for confirmation.
    for (const p of selectedProducts) {
      const qty = parseInt(p.requestedQuantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        Swal.fire({ title: 'Invalid quantity', text: `${p.name} has an invalid quantity.`, icon: 'error' });
        return;
      }
    }

    const itemsHtml = selectedProducts
      .map(p => `<tr>
        <td style="padding:4px 8px;text-align:left">${p.name}</td>
        <td style="padding:4px 8px;text-align:right">${p.requestedQuantity}</td>
      </tr>`).join('');
    const totalUnits = selectedProducts.reduce((s, p) => s + parseInt(p.requestedQuantity || 0), 0);

    const result = await Swal.fire({
      title: 'Submit requisition?',
      html: `<div style="text-align:left;font-size:0.88rem">
        <table style="width:100%;border-collapse:collapse;margin:6px 0">
          <thead><tr style="background:#f1f5f9;color:#475569">
            <th style="padding:6px 8px;text-align:left">Product</th>
            <th style="padding:6px 8px;text-align:right">Qty</th>
          </tr></thead>
          <tbody>${itemsHtml}</tbody>
          <tfoot><tr style="font-weight:700;color:#0f172a;border-top:2px solid #e2e8f0">
            <td style="padding:8px;text-align:right">Total units</td>
            <td style="padding:8px;text-align:right">${totalUnits.toLocaleString()}</td>
          </tr></tfoot>
        </table>
      </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: PRIMARY,
      cancelButtonColor: '#d32f2f',
      confirmButtonText: 'Yes, submit',
      cancelButtonText: 'Cancel',
      width: 540,
    });
    if (!result.isConfirmed) return;

    setSavingRequisition(true);
    try {
      const requisitionData = selectedProducts.map(product => ({
        productCode: product.name,
        productName: product.name,
        requestedQuantity: parseInt(product.requestedQuantity),
        quantityReceived: 0,
        currentBalance: product.currentBalance,
        remarks: product.remarks,
        requestedBy: userInfo.name || 'Unknown',
        requestedByUserId: userInfo.id || null,
        approvedBy: null,
        requestDate: new Date().toISOString().split('T')[0],
        status: 'pending',
      }));

      const response = await axios.post(saveRequisitions, { requisitions: requisitionData });
      if (response.data.success) {
        const savedRequisitions = response.data.data;
        const existing = JSON.parse(localStorage.getItem('requisitions')) || [];
        localStorage.setItem('requisitions', JSON.stringify([...existing, ...savedRequisitions]));
        setRequisitions(prev => [...prev, ...savedRequisitions]);

        Swal.fire({
          title: 'Submitted',
          text: `${selectedProducts.length} item${selectedProducts.length === 1 ? '' : 's'} sent for approval.`,
          icon: 'success', timer: 1800, showConfirmButton: false,
        });
        handleCloseDialog();
      } else {
        throw new Error(response.data.message || 'Failed to save requisitions');
      }
    } catch (err) {
      console.error('Error submitting requisition:', err);
      Swal.fire({ title: 'Error', text: 'Failed to submit requisition.', icon: 'error' });
    } finally {
      setSavingRequisition(false);
    }
  };

  // ── Action menu handlers (logic preserved) ─────────────────────────────
  const handleActionMenuOpen = (event, requisition) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRequisition(requisition);
  };
  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuRequisition(null);
  };
  const handleRequesterActionOpen = (action) => {
    handleActionMenuClose();
    setRequesterActionDialog({
      open: true, requisition: actionMenuRequisition, action, reason: '', processing: false,
    });
  };
  const handleRequesterActionClose = () => {
    setRequesterActionDialog({ open: false, requisition: null, action: '', reason: '', processing: false });
  };
  const handleRequesterAction = async () => {
    const { requisition, action, reason } = requesterActionDialog;
    if (action === 'reject' && !reason.trim()) {
      Swal.fire({ title: 'Reason required', text: 'Please provide a reason.', icon: 'error' });
      return;
    }
    setRequesterActionDialog(prev => ({ ...prev, processing: true }));
    try {
      const updateData = action === 'approve'
        ? { requesterApprovedBy: userInfo.name || 'Unknown' }
        : { requesterRejectedBy: userInfo.name || 'Unknown', requesterRejectionReason: reason };
      const response = await axios.put(updateRequisition(requisition.id), updateData);
      if (response.data.success) {
        setRequisitions(prev => prev.map(req => req.id === requisition.id ? { ...req, ...updateData } : req));
        Swal.fire({
          title: 'Done',
          text: `Requisition ${action === 'approve' ? 'approved' : 'rejected'}.`,
          icon: 'success', timer: 1600, showConfirmButton: false,
        });
        handleRequesterActionClose();
      } else {
        throw new Error(response.data.message || 'Failed to update');
      }
    } catch (err) {
      console.error('Error updating requisition:', err);
      Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to update.', icon: 'error' });
    } finally {
      setRequesterActionDialog(prev => ({ ...prev, processing: false }));
    }
  };

  const handleDeleteRequisition = async (requisitionId) => {
    const result = await Swal.fire({
      title: 'Delete this requisition?',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d32f2f', cancelButtonColor: MUTED,
      confirmButtonText: 'Yes, delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const response = await axios.delete(deleteRequisition(requisitionId));
      if (response.data.success) {
        setRequisitions(prev => prev.filter(req => req.id !== requisitionId));
        const saved = JSON.parse(localStorage.getItem('requisitions')) || [];
        localStorage.setItem('requisitions', JSON.stringify(saved.filter(req => req.id !== requisitionId)));
        Swal.fire({ title: 'Deleted', icon: 'success', timer: 1200, showConfirmButton: false });
      } else {
        throw new Error(response.data.message || 'Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting requisition:', err);
      Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to delete.', icon: 'error' });
    }
  };

  // ── Trace dialog handlers ──────────────────────────────────────────────
  const fetchProductTrace = async (productName, opts = {}) => {
    setTraceDialog(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axios.get(productTrace, {
        params: {
          name: productName,
          from: opts.from || undefined,
          to:   opts.to   || undefined,
        },
        timeout: 20000,
      });
      if (res.data?.success) {
        setTraceDialog(prev => ({ ...prev, loading: false, data: res.data, error: null }));
      } else {
        throw new Error(res.data?.message || 'Failed to load trace');
      }
    } catch (err) {
      console.error('Error loading product trace:', err);
      setTraceDialog(prev => ({
        ...prev, loading: false, data: null,
        error: err.response?.data?.message || err.message || 'Failed to load trace',
      }));
    }
  };

  const handleOpenTrace = (productName) => {
    setTraceDialog({
      open: true, productName, loading: true, data: null, error: null,
      from: '', to: '', requesterFilter: '',
    });
    fetchProductTrace(productName);
  };

  const handleCloseTrace = () => {
    setTraceDialog({
      open: false, productName: '', loading: false, data: null, error: null,
      from: '', to: '', requesterFilter: '',
    });
  };

  const handleApplyTraceFilters = () => {
    fetchProductTrace(traceDialog.productName, { from: traceDialog.from, to: traceDialog.to });
  };

  const handleResetTraceFilters = () => {
    setTraceDialog(prev => ({ ...prev, from: '', to: '', requesterFilter: '' }));
    fetchProductTrace(traceDialog.productName);
  };

  const handleClearReqFilters = () => {
    setDateFrom(''); setDateTo(''); setRequestedByFilter(''); setApprovedByFilter('');
    setStatusFilter('all'); setReqSearch('');
  };
  const hasReqFilters = !!(reqSearch || dateFrom || dateTo || requestedByFilter || approvedByFilter || statusFilter !== 'all');

  // ── Loading / error guards ─────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress size={40} sx={{ color: PRIMARY }} />
        <Typography sx={{ mt: 2, fontSize: '0.86rem', fontWeight: 600, color: MUTED }}>Loading dispensary…</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 500, width: '100%', borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  // ── Stats counters for the header subline ──────────────────────────────
  const reqCounts = {
    total: requisitions.length,
    pending: requisitions.filter(r => r.status === 'pending').length,
    approved: requisitions.filter(r => r.status === 'approved').length,
    rejected: requisitions.filter(r => r.status === 'rejected').length,
  };

  const cartTotalUnits = selectedProducts.reduce((s, p) => s + parseInt(p.requestedQuantity || 0), 0);

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh', py: 2.5 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
              Pharmacy · Requisitions
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
              Stock requisitions
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25, maxWidth: 760 }}>
              Browse current dispensary stock, raise a new requisition for items running low, and
              follow each request through approval. Use <strong>New Requisition</strong> to add
              several items at once.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh data" arrow>
              <IconButton
                size="small"
                onClick={() => { loadProducts(); loadRequisitions(); }}
                sx={{
                  border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
                  '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<ReceiptLongOutlined sx={{ fontSize: 18 }} />}
              onClick={handleOpenDialog}
              sx={{
                bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
                textTransform: 'none', fontWeight: 700, px: 2.5, boxShadow: 'none',
              }}
            >
              New Requisition
            </Button>
          </Stack>
        </Stack>

        {/* Tabs */}
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, mb: 2, bgcolor: '#fff' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              px: 1, minHeight: 44,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', minHeight: 44, color: MUTED },
              '& .Mui-selected': { color: PRIMARY },
              '& .MuiTabs-indicator': { bgcolor: PRIMARY, height: 2 },
            }}
          >
            <Tab label="My Products" icon={<InventoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab label="Requisitions" icon={<ReceiptLongOutlined sx={{ fontSize: 16 }} />} iconPosition="start" />
          </Tabs>
        </Paper>

        {/* ───── Tab 0: My Products ──────────────────────────────────── */}
        {activeTab === 0 && (
          <>
            {/* Stats strip */}
            <Grid container spacing={1.25} sx={{ mb: 2 }}>
              {[
                { label: 'Total products', value: stockStats.total, icon: <InventoryIcon fontSize="small" />, tint: '#f1f5f9',           color: STRONG    },
                { label: 'In stock',       value: stockStats.in,    icon: <TrendingUp     fontSize="small" />, tint: STOCK.in.tint,      color: STOCK.in.strong  },
                { label: 'Low stock',      value: stockStats.low,   icon: <WarningAmber   fontSize="small" />, tint: STOCK.low.tint,     color: STOCK.low.strong },
                { label: 'Out of stock',   value: stockStats.out,   icon: <ErrorOutline   fontSize="small" />, tint: STOCK.out.tint,     color: STOCK.out.strong },
              ].map(t => (
                <Grid item xs={6} md={3} key={t.label}>
                  <Paper elevation={0} sx={{
                    p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                  }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 1.5,
                      bgcolor: t.tint, color: t.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {t.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={LABEL_SX}>{t.label}</Typography>
                      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }}>
                        {Number(t.value || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper elevation={0} sx={{
              border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff',
            }}>
              {/* Filter row */}
              <Box sx={{
                p: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff',
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
              }}>
                <TextField
                  size="small"
                  placeholder="Search products by name…"
                  value={productsSearchTerm}
                  onChange={e => setProductsSearchTerm(e.target.value)}
                  sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 0 320px' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                    endAdornment: productsSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setProductsSearchTerm('')}>
                          <Clear sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{
                  display: 'inline-flex', p: 0.4, borderRadius: 1.5,
                  bgcolor: '#f1f5f9', border: `1px solid ${BORDER}`,
                }}>
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'in',  label: 'In stock' },
                    { value: 'low', label: 'Low' },
                    { value: 'out', label: 'Out' },
                  ].map(({ value, label }) => {
                    const selected = stockFilter === value;
                    return (
                      <Button
                        key={value}
                        onClick={() => setStockFilter(value)}
                        size="small"
                        sx={{
                          minWidth: 76, height: 30, px: 1.25, borderRadius: 1,
                          textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
                          color: selected ? '#fff' : MUTED,
                          bgcolor: selected ? PRIMARY : 'transparent',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: selected ? PRIMARY_DARK : 'rgba(15,23,42,0.04)', boxShadow: 'none' },
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Box>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                  {filteredMyProducts.length} of {products.length} shown
                </Typography>
              </Box>

              {/* Table */}
              <TableContainer sx={{ maxHeight: '60vh' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1.25 } }}>
                      <TableCell>Product</TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>Current stock</TableCell>
                      <TableCell align="right" sx={{ width: 130 }}>Reorder level</TableCell>
                      <TableCell align="center" sx={{ width: 150 }}>Status</TableCell>
                      <TableCell align="right" sx={{ width: 220 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMyProducts
                      .slice(myProductsPage * myProductsRowsPerPage, myProductsPage * myProductsRowsPerPage + myProductsRowsPerPage)
                      .map((product) => {
                        const status = getStockStatus(product);
                        const stk = STOCK[status];
                        return (
                          <TableRow key={product.name} hover>
                            <TableCell sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                              {product.name}
                            </TableCell>
                            <TableCell align="right" sx={{
                              fontSize: '0.92rem', fontWeight: 800,
                              color: status === 'out' ? STOCK.out.strong : STRONG,
                              fontVariantNumeric: 'tabular-nums',
                            }}>
                              {product.currentBalance}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.84rem', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                              {product.st_level ?? '—'}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                px: 1, py: 0.25, borderRadius: 1,
                                bgcolor: stk.tint, color: stk.strong,
                                fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                              }}>
                                {stk.label}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                <Tooltip title="Trace this product's full trail" arrow>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<TimelineIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => handleOpenTrace(product.name)}
                                    sx={{
                                      textTransform: 'none', fontWeight: 700,
                                      borderColor: BORDER, color: MUTED,
                                      '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: PRIMARY_BG },
                                    }}
                                  >
                                    Trace
                                  </Button>
                                </Tooltip>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => {
                                    handleOpenDialog();
                                    // Pre-fill a sensible quantity (gap to reorder level) so the
                                    // user can confirm with one click in the dialog.
                                    const suggestion = Math.max(1, (product.st_level || 0) - (product.currentBalance || 0));
                                    setRowQty({ [product.name]: String(suggestion) });
                                    setSearchTerm(product.name);
                                  }}
                                  sx={{
                                    textTransform: 'none', fontWeight: 700, boxShadow: 'none',
                                    bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                                  }}
                                >
                                  Requisition
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredMyProducts.length}
                page={myProductsPage}
                onPageChange={(_, p) => setMyProductsPage(p)}
                rowsPerPage={myProductsRowsPerPage}
                onRowsPerPageChange={(e) => { setMyProductsRowsPerPage(parseInt(e.target.value, 10)); setMyProductsPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: `1px solid ${BORDER}`,
                  '.MuiTablePagination-toolbar': { minHeight: 44 },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.78rem', color: MUTED },
                  '.MuiTablePagination-select': { fontSize: '0.78rem', fontWeight: 700, color: STRONG },
                }}
              />

              {filteredMyProducts.length === 0 && (
                <Box sx={{ p: 5, textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
                  <InventoryIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>No products found</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
                    {productsSearchTerm ? 'Try a different search.' : 'Nothing to show in this stock band.'}
                  </Typography>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* ───── Tab 1: Requisitions ────────────────────────────────── */}
        {activeTab === 1 && (
          <>
            {/* Stats */}
            <Grid container spacing={1.25} sx={{ mb: 2 }}>
              {[
                { label: 'Total',    value: reqCounts.total,    icon: <ReceiptLongOutlined fontSize="small" />, tint: '#f1f5f9',          color: STRONG },
                { label: 'Pending',  value: reqCounts.pending,  icon: <HourglassTop        fontSize="small" />, tint: STATUS.pending.tint, color: STATUS.pending.strong },
                { label: 'Approved', value: reqCounts.approved, icon: <CheckCircleOutline  fontSize="small" />, tint: STATUS.approved.tint, color: STATUS.approved.strong },
                { label: 'Rejected', value: reqCounts.rejected, icon: <HighlightOff        fontSize="small" />, tint: STATUS.rejected.tint, color: STATUS.rejected.strong },
              ].map(t => (
                <Grid item xs={6} md={3} key={t.label}>
                  <Paper elevation={0} sx={{
                    p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                  }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 1.5,
                      bgcolor: t.tint, color: t.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {t.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={LABEL_SX}>{t.label}</Typography>
                      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }}>
                        {t.value}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper elevation={0} sx={{
              border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff',
            }}>
              {/* Filter bar */}
              <Box sx={{
                p: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff',
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
              }}>
                <TextField
                  size="small" placeholder="Search by product or requester…"
                  value={reqSearch}
                  onChange={e => setReqSearch(e.target.value)}
                  sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 0 280px' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <MenuItem value="all">All status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <TextField size="small" type="date" label="From"
                  value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                <TextField size="small" type="date" label="To"
                  value={dateTo} onChange={e => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Requested by</InputLabel>
                  <Select label="Requested by" value={requestedByFilter} onChange={e => setRequestedByFilter(e.target.value)}>
                    <MenuItem value=""><em>All</em></MenuItem>
                    {distinctRequestedBy.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Approved by</InputLabel>
                  <Select label="Approved by" value={approvedByFilter} onChange={e => setApprovedByFilter(e.target.value)}>
                    <MenuItem value=""><em>All</em></MenuItem>
                    {distinctApprovedBy.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
                {hasReqFilters && (
                  <Button size="small" onClick={handleClearReqFilters} startIcon={<Clear sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: 'none', color: MUTED, fontSize: '0.78rem', '&:hover': { bgcolor: '#f1f5f9' } }}>
                    Clear
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                  {filteredRequisitions.length} item{filteredRequisitions.length === 1 ? '' : 's'} · {groupedRequisitions.length} group{groupedRequisitions.length === 1 ? '' : 's'}
                </Typography>
              </Box>

              {/* Grouped history — one row per (requester · date) */}
              {groupedRequisitions.length === 0 ? (
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <ReceiptLongOutlined sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>No requisitions</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
                    {hasReqFilters ? 'Try clearing the filters.' : 'Raise your first requisition with the button above.'}
                  </Typography>
                </Box>
              ) : (
                groupedRequisitions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((group) => {
                    const isOpen = expandedGroups.has(group.key);
                    const colour = colourFor(group.requester);
                    const isMine = group.requester === currentUserName;

                    return (
                      <Box key={group.key} sx={{ borderBottom: `1px solid ${BORDER}` }}>
                        {/* Group header row */}
                        <Box
                          onClick={() => toggleGroup(group.key)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            px: 1.5, py: 1.25, cursor: 'pointer',
                            bgcolor: isOpen ? '#f8fafc' : '#fff',
                            borderLeft: `3px solid ${group.pending > 0 ? STATUS.pending.color : 'transparent'}`,
                            '&:hover': { bgcolor: '#f8fafc' },
                            transition: 'background-color 0.12s',
                          }}
                        >
                          <Avatar sx={{
                            width: 38, height: 38,
                            bgcolor: colour.bg, color: colour.fg,
                            fontSize: '0.85rem', fontWeight: 800,
                          }}>
                            {initialsOf(group.requester)}
                          </Avatar>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.4 }}>
                              <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }} noWrap>
                                {group.requester}
                              </Typography>
                              {isMine && (
                                <Box sx={{
                                  px: 0.6, py: 0.05, borderRadius: 0.6,
                                  fontSize: '0.6rem', fontWeight: 800,
                                  textTransform: 'uppercase', letterSpacing: '0.4px',
                                  bgcolor: PRIMARY_BG, color: PRIMARY_DARK,
                                }}>
                                  You
                                </Box>
                              )}
                            </Stack>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.4, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontWeight: 600 }}>
                                {group.totalLines} item{group.totalLines === 1 ? '' : 's'} · {group.totalUnits.toLocaleString()} unit{group.totalUnits === 1 ? '' : 's'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                · {group.date}
                              </Typography>
                            </Stack>
                          </Box>

                          {/* Status counts */}
                          <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' } }}>
                            {group.pending > 0 && (
                              <Box sx={{
                                px: 0.85, py: 0.25, borderRadius: 0.85,
                                bgcolor: STATUS.pending.tint, color: STATUS.pending.strong,
                                fontSize: '0.7rem', fontWeight: 700,
                              }}>
                                {group.pending} pending
                              </Box>
                            )}
                            {group.approved > 0 && (
                              <Box sx={{
                                px: 0.85, py: 0.25, borderRadius: 0.85,
                                bgcolor: STATUS.approved.tint, color: STATUS.approved.strong,
                                fontSize: '0.7rem', fontWeight: 700,
                              }}>
                                {group.approved} approved
                              </Box>
                            )}
                            {group.rejected > 0 && (
                              <Box sx={{
                                px: 0.85, py: 0.25, borderRadius: 0.85,
                                bgcolor: STATUS.rejected.tint, color: STATUS.rejected.strong,
                                fontSize: '0.7rem', fontWeight: 700,
                              }}>
                                {group.rejected} rejected
                              </Box>
                            )}
                          </Stack>

                          <IconButton size="small" sx={{ color: MUTED }}>
                            {isOpen ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
                          </IconButton>
                        </Box>

                        {/* Expanded items table */}
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1 } }}>
                                  <TableCell sx={{ width: 28, pl: 7 }} />
                                  <TableCell>Product</TableCell>
                                  <TableCell align="right" sx={{ width: 80 }}>Qty req.</TableCell>
                                  <TableCell align="right" sx={{ width: 80 }}>Qty rec.</TableCell>
                                  <TableCell sx={{ width: 140 }}>Requester action</TableCell>
                                  <TableCell sx={{ width: 130 }}>Approved by</TableCell>
                                  <TableCell sx={{ width: 200 }}>Reason / note</TableCell>
                                  <TableCell align="center" sx={{ width: 110 }}>Status</TableCell>
                                  <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.items.map((requisition, index) => {
                                  const isPending  = requisition.status === 'pending';
                                  const ownsThis   = requisition.requestedBy === currentUserName;
                                  const canDelete  = isPending && ownsThis;

                                  return (
                                    <TableRow key={requisition.id || index} hover sx={{ borderBottom: `1px solid ${BORDER}` }}>
                                      <TableCell sx={{ pl: 7, color: MUTED, fontSize: '0.74rem', fontWeight: 700 }}>
                                        {index + 1}
                                      </TableCell>
                                      <TableCell>
                                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                                          {requisition.productName}
                                        </Typography>
                                        {requisition.remarks && (
                                          <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontStyle: 'italic', mt: 0.25 }}>
                                            “{requisition.remarks}”
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums', fontSize: '0.84rem' }}>
                                        {requisition.requestedQuantity}
                                      </TableCell>
                                      <TableCell align="right" sx={{
                                        fontWeight: 600,
                                        color: requisition.quantityReceived ? PRIMARY_DARK : MUTED,
                                        fontVariantNumeric: 'tabular-nums', fontSize: '0.84rem',
                                      }}>
                                        {requisition.quantityReceived || 0}
                                      </TableCell>
                                      <TableCell>
                                        {requisition.requesterApprovedBy ? (
                                          <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                            px: 0.85, py: 0.2, borderRadius: 0.85,
                                            bgcolor: STATUS.approved.tint, color: STATUS.approved.strong,
                                            fontSize: '0.7rem', fontWeight: 700,
                                          }}>
                                            ✓ {requisition.requesterApprovedBy}
                                          </Box>
                                        ) : requisition.requesterRejectedBy ? (
                                          <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                            px: 0.85, py: 0.2, borderRadius: 0.85,
                                            bgcolor: STATUS.rejected.tint, color: STATUS.rejected.strong,
                                            fontSize: '0.7rem', fontWeight: 700,
                                          }}>
                                            ✗ {requisition.requesterRejectedBy}
                                          </Box>
                                        ) : (
                                          <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>—</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem', color: STRONG }}>
                                        {requisition.approvedBy || '—'}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.78rem', color: MUTED }}>
                                        {requisition.requesterRejectionReason
                                          ? <span style={{ color: STATUS.rejected.strong }}>{requisition.requesterRejectionReason}</span>
                                          : (requisition.reason || '—')}
                                      </TableCell>
                                      <TableCell align="center">
                                        <StatusPill status={requisition.status} />
                                      </TableCell>
                                      <TableCell align="right">
                                        {isPending ? (
                                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <Tooltip title="More actions" arrow>
                                              <IconButton size="small" onClick={(e) => handleActionMenuOpen(e, requisition)}
                                                sx={{ color: MUTED }}>
                                                <MoreVertIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </Tooltip>
                                            {canDelete ? (
                                              <Tooltip title="Delete this line" arrow>
                                                <IconButton size="small" onClick={() => handleDeleteRequisition(requisition.id)}
                                                  sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}>
                                                  <DeleteOutline sx={{ fontSize: 16 }} />
                                                </IconButton>
                                              </Tooltip>
                                            ) : (
                                              <Tooltip
                                                arrow
                                                title={
                                                  <Box sx={{ p: 0.25 }}>
                                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, display: 'block' }}>
                                                      Cannot delete
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.7rem', display: 'block', opacity: 0.85 }}>
                                                      Only {requisition.requestedBy || 'the requester'} can remove this line.
                                                    </Typography>
                                                  </Box>
                                                }
                                              >
                                                <span>
                                                  <IconButton size="small" disabled sx={{ color: '#cbd5e1' }}>
                                                    <LockOutlined sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            )}
                                          </Stack>
                                        ) : (
                                          <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>—</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })
              )}

              {/* Pagination over groups */}
              {groupedRequisitions.length > 0 && (
                <TablePagination
                  component="div"
                  count={groupedRequisitions.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Groups per page:"
                  sx={{
                    borderTop: `1px solid ${BORDER}`,
                    '.MuiTablePagination-toolbar': { minHeight: 44 },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.78rem', color: MUTED },
                    '.MuiTablePagination-select': { fontSize: '0.78rem', fontWeight: 700, color: STRONG },
                  }}
                />
              )}
            </Paper>
          </>
        )}
      </Container>

      {/* ── New Requisition Dialog ──────────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { height: '92vh', width: '94vw', maxWidth: 'none', borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          py: 1.75, px: 2.5,
          bgcolor: PRIMARY, color: '#fff',
        }}>
          <ReceiptLongOutlined sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              New Requisition
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              Type a quantity next to each medication and click <strong>Add</strong>. Submit when you're done.
            </Typography>
          </Box>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 1.1, py: 0.5, borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.18)', color: '#fff',
            fontSize: '0.78rem', fontWeight: 800,
          }}>
            {selectedProducts.length} item{selectedProducts.length === 1 ? '' : 's'} · {cartTotalUnits} unit{cartTotalUnits === 1 ? '' : 's'}
          </Box>
          <IconButton onClick={handleCloseDialog} disabled={savingRequisition} sx={{ color: '#fff' }} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, display: 'flex', height: 'calc(92vh - 110px)', bgcolor: '#f8fafc' }}>

          {/* Left – Dispensary stock with inline qty + Add per row */}
          <Box sx={{ width: '54%', borderRight: `1px solid ${BORDER}`, p: 1.75, bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Box>
                <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Dispensary stock</Typography>
                <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                  Type a quantity next to a product, then click <strong>Add</strong>.
                </Typography>
              </Box>
              <Box sx={{
                px: 1, py: 0.25, borderRadius: 1, bgcolor: '#f1f5f9', color: MUTED,
                fontSize: '0.72rem', fontWeight: 700,
              }}>
                {filteredProducts.length} item{filteredProducts.length === 1 ? '' : 's'}
              </Box>
            </Stack>

            <TextField
              fullWidth size="small" placeholder="Search products…"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.25 }}
            />

            <Box sx={{ flexGrow: 1, overflow: 'auto', borderRadius: 1.5, border: `1px solid ${BORDER}` }}>
              {filteredProducts.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 220, color: '#94a3b8' }}>
                  <SearchIcon sx={{ fontSize: 36, mb: 0.75 }} />
                  <Typography sx={{ fontSize: '0.82rem' }}>No products found</Typography>
                </Box>
              ) : (
                filteredProducts.map((product, idx) => {
                  const status = getStockStatus(product);
                  const stk = STOCK[status];
                  const inCart = selectedProducts.some(p => p.name === product.name);
                  const typed  = rowQty[product.name] ?? '';
                  const typedNum = parseInt(typed);
                  const canAdd = Number.isFinite(typedNum) && typedNum > 0;
                  return (
                    <Box
                      key={product.name || idx}
                      sx={{
                        px: 1.25, py: 1,
                        borderBottom: `1px solid ${BORDER}`,
                        bgcolor: inCart ? '#f0fdf4' : '#fff',
                        borderLeft: `3px solid ${inCart ? PRIMARY : 'transparent'}`,
                        transition: 'background-color 0.12s',
                        '&:hover': { bgcolor: inCart ? '#dcfce7' : '#f8fafc' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {/* Identity */}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: STRONG, fontSize: '0.86rem', lineHeight: 1.25 }} noWrap title={product.name}>
                            {product.name}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.4, flexWrap: 'wrap', gap: 0.4 }}>
                            <Box sx={{
                              px: 0.6, py: 0.05, borderRadius: 0.6,
                              fontSize: '0.62rem', fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.4px',
                              bgcolor: stk.tint, color: stk.strong,
                            }}>
                              {stk.label}
                            </Box>
                            {inCart && (
                              <Box sx={{
                                px: 0.6, py: 0.05, borderRadius: 0.6,
                                fontSize: '0.62rem', fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.4px',
                                bgcolor: PRIMARY_BG, color: PRIMARY_DARK,
                              }}>
                                in cart
                              </Box>
                            )}
                          </Stack>
                        </Box>

                        {/* Stock count */}
                        <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 56 }}>
                          <Typography sx={LABEL_SX}>Stock</Typography>
                          <Typography sx={{
                            fontSize: '0.95rem', fontWeight: 800,
                            color: status === 'out' ? STOCK.out.strong : STRONG,
                            lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
                          }}>
                            {product.currentBalance}
                          </Typography>
                        </Box>

                        {/* Inline qty input */}
                        <TextField
                          size="small" type="number"
                          placeholder="Qty"
                          value={typed}
                          onChange={(e) => setRowQty(prev => ({ ...prev, [product.name]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && canAdd) {
                              e.preventDefault();
                              handleAddRow(product);
                            }
                          }}
                          inputProps={{ min: 1, style: { textAlign: 'center', fontWeight: 700, padding: '6px 6px' } }}
                          sx={{ width: 78, flexShrink: 0, '& .MuiOutlinedInput-root': { fontVariantNumeric: 'tabular-nums', bgcolor: '#fff' } }}
                        />

                        {/* Inline add button */}
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddRow(product)}
                          disabled={!canAdd}
                          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            minWidth: 86, height: 32,
                            textTransform: 'none', fontWeight: 700, boxShadow: 'none',
                            bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                          }}
                        >
                          {inCart ? 'Update' : 'Add'}
                        </Button>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>

          {/* Right – Cart */}
          <Box sx={{ width: '46%', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Cart */}
            <Paper elevation={0} sx={{
              flexGrow: 1, borderRadius: 2, border: `1px solid ${BORDER}`, overflow: 'hidden',
              display: 'flex', flexDirection: 'column', bgcolor: '#fff',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 1.75, py: 1.1, bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <ReceiptLongOutlined sx={{ fontSize: 16, color: PRIMARY }} />
                  <Typography sx={LABEL_SX}>Requisition cart</Typography>
                </Stack>
                <Box sx={{
                  px: 1, py: 0.25, borderRadius: 1, bgcolor: PRIMARY_BG, color: PRIMARY,
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  {selectedProducts.length} line{selectedProducts.length === 1 ? '' : 's'}
                </Box>
              </Stack>

              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {selectedProducts.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200, color: '#94a3b8', p: 3 }}>
                    <ReceiptLongOutlined sx={{ fontSize: 38, mb: 1 }} />
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>Empty cart</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                      Type a quantity next to a product on the left, then click <strong>Add</strong>.
                    </Typography>
                  </Box>
                ) : (
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1 } }}>
                        <TableCell sx={{ width: 40 }}>#</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell align="right" sx={{ width: 90 }}>Stock</TableCell>
                        <TableCell align="center" sx={{ width: 110 }}>Qty</TableCell>
                        <TableCell sx={{ width: 220 }}>Remarks</TableCell>
                        <TableCell align="center" sx={{ width: 56 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProducts.map((p, index) => {
                        const qtyNum = parseInt(p.requestedQuantity);
                        const invalid = !Number.isFinite(qtyNum) || qtyNum <= 0;
                        const status = getStockStatus(p);
                        const stk = STOCK[status];
                        return (
                          <TableRow key={`${p.name}-${index}`} sx={{ '&:hover': { bgcolor: '#f8fafc' }, borderBottom: `1px solid ${BORDER}` }}>
                            <TableCell sx={{ color: MUTED, fontWeight: 700, fontSize: '0.78rem' }}>{index + 1}</TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>{p.name}</Typography>
                              <Box sx={{
                                display: 'inline-flex', mt: 0.3,
                                px: 0.6, py: 0.05, borderRadius: 0.6,
                                fontSize: '0.62rem', fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.4px',
                                bgcolor: stk.tint, color: stk.strong,
                              }}>
                                {stk.label}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums', fontSize: '0.82rem' }}>
                              {p.currentBalance}
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small" type="number"
                                value={p.requestedQuantity}
                                onChange={(e) => handleCartQtyChange(index, e.target.value)}
                                error={invalid}
                                inputProps={{ min: 1, style: { textAlign: 'center', fontWeight: 700, padding: '4px 6px' } }}
                                sx={{ width: 90, '& .MuiOutlinedInput-root': { fontVariantNumeric: 'tabular-nums' } }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small" fullWidth
                                value={p.remarks || ''}
                                onChange={(e) => setSelectedProducts(prev => {
                                  const next = [...prev];
                                  next[index] = { ...next[index], remarks: e.target.value };
                                  return next;
                                })}
                                placeholder="—"
                                inputProps={{ style: { padding: '4px 8px', fontSize: '0.78rem' } }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Remove" arrow>
                                <IconButton size="small" onClick={() => handleRemoveProductFromList(index)}
                                  sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}>
                                  <DeleteOutline sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>

              {/* Totals strip */}
              <Box sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: '#f8fafc', px: 2, py: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography sx={LABEL_SX}>Lines</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG, lineHeight: 1.1 }}>
                        {selectedProducts.length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={LABEL_SX}>Total units</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: PRIMARY, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                        {cartTotalUnits.toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
                    Requested by <strong>{userInfo.name || 'Unknown'}</strong>
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: '0.78rem', color: MUTED, mr: 'auto', pl: 1.5 }}>
            {selectedProducts.length === 0
              ? 'Add at least one line to submit.'
              : `Ready — ${selectedProducts.length} line${selectedProducts.length === 1 ? '' : 's'} · ${cartTotalUnits} unit${cartTotalUnits === 1 ? '' : 's'}.`}
          </Typography>
          <Button
            onClick={handleCloseDialog}
            disabled={savingRequisition}
            sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRequisition}
            variant="contained"
            disabled={selectedProducts.length === 0 || savingRequisition}
            startIcon={savingRequisition
              ? <CircularProgress size={16} sx={{ color: 'white' }} />
              : <ReceiptLongOutlined sx={{ fontSize: 18 }} />}
            sx={{
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
              textTransform: 'none', fontWeight: 700, px: 3, boxShadow: 'none',
            }}
          >
            {savingRequisition ? 'Submitting…' : `Submit (${selectedProducts.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Requester action dialog (approve/reject own requisition) ───── */}
      <Dialog
        open={requesterActionDialog.open}
        onClose={handleRequesterActionClose}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 2.5,
          bgcolor: requesterActionDialog.action === 'approve' ? PRIMARY : '#dc2626',
          color: '#fff',
        }}>
          {requesterActionDialog.action === 'approve'
            ? <CheckCircleOutline sx={{ fontSize: 22 }} />
            : <HighlightOff sx={{ fontSize: 22 }} />}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              {requesterActionDialog.action === 'approve' ? 'Approve your requisition' : 'Reject your requisition'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {requesterActionDialog.action === 'approve'
                ? 'Confirm before sending it for manager approval.'
                : 'Provide a reason — this will be recorded against the line.'}
            </Typography>
          </Box>
          <IconButton onClick={handleRequesterActionClose} disabled={requesterActionDialog.processing} sx={{ color: '#fff' }} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {requesterActionDialog.requisition && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG, mb: 0.5 }}>
                {requesterActionDialog.requisition.productName}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
                  Quantity: <strong style={{ color: STRONG }}>{requesterActionDialog.requisition.requestedQuantity}</strong>
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
                  Date: <strong style={{ color: STRONG }}>{requesterActionDialog.requisition.requestDate}</strong>
                </Typography>
              </Stack>
            </Paper>
          )}

          {requesterActionDialog.action === 'reject' ? (
            <TextField
              fullWidth size="small"
              label="Reason for rejection"
              multiline rows={3}
              value={requesterActionDialog.reason}
              onChange={(e) => setRequesterActionDialog(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Why are you rejecting this requisition?"
              required
            />
          ) : (
            <Alert severity="info" sx={{ borderRadius: 1.5 }}>
              You are confirming this requisition. It will be sent to the procurement department for the next approval step.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={handleRequesterActionClose} disabled={requesterActionDialog.processing}
            sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}>
            Cancel
          </Button>
          <Button
            onClick={handleRequesterAction}
            variant="contained"
            disabled={requesterActionDialog.processing}
            startIcon={requesterActionDialog.processing
              ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
              : (requesterActionDialog.action === 'approve' ? <CheckCircleOutline /> : <HighlightOff />)}
            sx={{
              bgcolor: requesterActionDialog.action === 'approve' ? PRIMARY : '#dc2626',
              '&:hover': { bgcolor: requesterActionDialog.action === 'approve' ? PRIMARY_DARK : '#b91c1c' },
              textTransform: 'none', fontWeight: 700, px: 3, boxShadow: 'none',
            }}
          >
            {requesterActionDialog.processing
              ? (requesterActionDialog.action === 'approve' ? 'Approving…' : 'Rejecting…')
              : (requesterActionDialog.action === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        PaperProps={{ sx: { borderRadius: 1.5, border: `1px solid ${BORDER}`, boxShadow: '0 6px 24px rgba(15,23,42,0.08)' } }}
      >
        {actionMenuRequisition?.status === 'approved' && (
          <MenuItem onClick={() => handleRequesterActionOpen('approve')} sx={{ fontSize: '0.84rem', color: STATUS.approved.strong }}>
            <CheckCircleOutline sx={{ mr: 1, fontSize: 18 }} /> Approve
          </MenuItem>
        )}
        <MenuItem onClick={() => handleRequesterActionOpen('reject')} sx={{ fontSize: '0.84rem', color: STATUS.rejected.strong }}>
          <HighlightOff sx={{ mr: 1, fontSize: 18 }} /> Reject
        </MenuItem>
      </Menu>

      {/* ── Product Trace Dialog ────────────────────────────────────────── */}
      <Dialog
        open={traceDialog.open}
        onClose={handleCloseTrace}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { height: '94vh', width: '95vw', maxWidth: 'none', borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* Header */}
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          py: 1.75, px: 2.5,
          bgcolor: PRIMARY, color: '#fff',
        }}>
          <TimelineIcon sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, opacity: 0.9, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Product trail
            </Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {traceDialog.productName}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              How it came in (left) and how it went out (right) — with names and dates at every step.
            </Typography>
          </Box>
          <IconButton onClick={handleCloseTrace} sx={{ color: '#fff' }} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>

          {/* Filter bar */}
          <Box sx={{
            p: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
          }}>
            <TextField
              size="small" type="date" label="From"
              value={traceDialog.from}
              onChange={(e) => setTraceDialog(prev => ({ ...prev, from: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
            />
            <TextField
              size="small" type="date" label="To"
              value={traceDialog.to}
              onChange={(e) => setTraceDialog(prev => ({ ...prev, to: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Requester</InputLabel>
              <Select
                label="Requester"
                value={traceDialog.requesterFilter}
                onChange={(e) => setTraceDialog(prev => ({ ...prev, requesterFilter: e.target.value }))}
              >
                <MenuItem value=""><em>All requesters</em></MenuItem>
                {(traceDialog.data?.requesters || []).map(n => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small" variant="contained" onClick={handleApplyTraceFilters}
              disabled={traceDialog.loading}
              sx={{
                textTransform: 'none', fontWeight: 700, boxShadow: 'none',
                bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
              }}
            >
              Apply
            </Button>
            <Button
              size="small" onClick={handleResetTraceFilters}
              disabled={traceDialog.loading}
              startIcon={<Clear sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
            >
              Reset
            </Button>
            <Box sx={{ flex: 1 }} />
            {/* Totals strip */}
            {traceDialog.data && (
              <Stack direction="row" spacing={2}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={LABEL_SX}>In</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STATUS.approved.strong, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {(traceDialog.data.totals?.in || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={LABEL_SX}>Out</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0369A1', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {(traceDialog.data.totals?.out || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={LABEL_SX}>Balance</Typography>
                  <Typography sx={{
                    fontSize: '1rem', fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
                    color: (traceDialog.data.totals?.balance ?? 0) < 0 ? STATUS.rejected.strong : PRIMARY_DARK,
                  }}>
                    {(traceDialog.data.totals?.balance ?? 0).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Body */}
          {traceDialog.loading ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={36} sx={{ color: PRIMARY }} />
              <Typography sx={{ mt: 1.5, fontSize: '0.84rem', color: MUTED, fontWeight: 600 }}>
                Loading trail…
              </Typography>
            </Box>
          ) : traceDialog.error ? (
            <Box sx={{ p: 5 }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>{traceDialog.error}</Alert>
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* ───── LEFT — Stock IN ───── */}
              <Box sx={{ width: '50%', borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{
                  px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`,
                  bgcolor: '#fff',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.25,
                    bgcolor: STATUS.approved.tint, color: STATUS.approved.strong,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <ArrowDownward sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={LABEL_SX}>Stock in</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }}>
                      Requisitions &amp; direct entries
                    </Typography>
                  </Box>
                  <Box sx={{
                    px: 0.85, py: 0.25, borderRadius: 0.85,
                    bgcolor: STATUS.approved.tint, color: STATUS.approved.strong,
                    fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                  }}>
                    {((traceDialog.data?.requisitions || []).filter(r => !traceDialog.requesterFilter || r.requestedBy === traceDialog.requesterFilter).length) + (traceDialog.data?.directInflows?.length || 0)} events
                  </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                  {/* Vertical timeline rail */}
                  <Box sx={{
                    position: 'absolute', top: 16, bottom: 16, left: 30,
                    width: 2, bgcolor: STATUS.approved.tint, borderRadius: 1,
                  }} />

                  <Stack spacing={1.25} sx={{ p: 2 }}>
                    {(() => {
                      const filtered = (traceDialog.data?.requisitions || [])
                        .filter(r => !traceDialog.requesterFilter || r.requestedBy === traceDialog.requesterFilter);
                      const directs = traceDialog.data?.directInflows || [];

                      if (filtered.length === 0 && directs.length === 0) {
                        return (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <ArrowDownward sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                              No inflow events
                            </Typography>
                            <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                              No requisitions or direct stock entries match these filters.
                            </Typography>
                          </Box>
                        );
                      }

                      // Render requisitions first
                      const cards = [];
                      filtered.forEach((r) => {
                        const sPalette = STATUS[r.status] || STATUS.pending;
                        const requester = r.requestedBy || 'Unknown';
                        const colour = colourFor(requester);
                        // Old data: requester == approver if no separate approver set.
                        const approver = r.approvedBy || (r.requesterApprovedBy && r.requesterApprovedBy !== requester
                          ? r.requesterApprovedBy
                          : null);
                        const isSelfApproved = !approver;
                        const qty = parseInt(r.received || r.requested || 0);
                        cards.push(
                          <Box key={`req-${r.id}`} sx={{ position: 'relative', pl: 4 }}>
                            {/* Timeline dot */}
                            <Box sx={{
                              position: 'absolute', left: 11, top: 14,
                              width: 14, height: 14, borderRadius: '50%',
                              bgcolor: sPalette.color, border: '3px solid #fff',
                              boxShadow: `0 0 0 2px ${sPalette.tint}`,
                            }} />
                            <Paper elevation={0} sx={{
                              p: 1.5, borderRadius: 2,
                              border: `1px solid ${BORDER}`, bgcolor: '#fff',
                              borderLeft: `4px solid ${sPalette.color}`,
                            }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                                      {r.date}
                                    </Typography>
                                    <StatusPill status={r.status} />
                                  </Stack>
                                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                                    <ArrowDownward sx={{ fontSize: 16, color: STATUS.approved.strong }} />
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: STATUS.approved.strong, fontVariantNumeric: 'tabular-nums' }}>
                                      +{qty.toLocaleString()}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem', color: MUTED, fontWeight: 600 }}>
                                      unit{qty === 1 ? '' : 's'}
                                    </Typography>
                                    {parseInt(r.requested) !== qty && (
                                      <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontStyle: 'italic' }}>
                                        (requested {r.requested})
                                      </Typography>
                                    )}
                                  </Stack>

                                  {/* Requester / approver row */}
                                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                      <Avatar sx={{
                                        width: 22, height: 22,
                                        bgcolor: colour.bg, color: colour.fg,
                                        fontSize: '0.62rem', fontWeight: 800,
                                      }}>
                                        {initialsOf(requester)}
                                      </Avatar>
                                      <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                          Requested by
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: STRONG, lineHeight: 1.1 }}>
                                          {requester}
                                        </Typography>
                                      </Box>
                                    </Stack>

                                    <Box sx={{ color: MUTED, mx: 0.5 }}>→</Box>

                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                      {approver ? (() => {
                                        const c2 = colourFor(approver);
                                        return (
                                          <>
                                            <Avatar sx={{
                                              width: 22, height: 22,
                                              bgcolor: c2.bg, color: c2.fg,
                                              fontSize: '0.62rem', fontWeight: 800,
                                            }}>
                                              {initialsOf(approver)}
                                            </Avatar>
                                            <Box>
                                              <Typography sx={{ fontSize: '0.65rem', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                                Approved by
                                              </Typography>
                                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: STRONG, lineHeight: 1.1 }}>
                                                {approver}
                                              </Typography>
                                            </Box>
                                          </>
                                        );
                                      })() : isSelfApproved ? (
                                        <Box sx={{
                                          px: 0.85, py: 0.25, borderRadius: 0.85,
                                          bgcolor: '#fef3c7', color: '#92400E',
                                          fontSize: '0.66rem', fontWeight: 700,
                                          textTransform: 'uppercase', letterSpacing: 0.4,
                                        }}>
                                          Self-approved (legacy)
                                        </Box>
                                      ) : (
                                        <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontStyle: 'italic' }}>
                                          Awaiting approval
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Stack>

                                  {r.remarks && (
                                    <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontStyle: 'italic', mt: 0.75 }}>
                                      “{r.remarks}”
                                    </Typography>
                                  )}
                                  {r.requesterRejectionReason && (
                                    <Typography sx={{ fontSize: '0.74rem', color: STATUS.rejected.strong, mt: 0.5, fontWeight: 600 }}>
                                      Rejection reason: {r.requesterRejectionReason}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          </Box>
                        );
                      });

                      // Then direct dispensary entries
                      directs.forEach((d) => {
                        cards.push(
                          <Box key={`dir-${d.trans_code}`} sx={{ position: 'relative', pl: 4 }}>
                            <Box sx={{
                              position: 'absolute', left: 11, top: 14,
                              width: 14, height: 14, borderRadius: '50%',
                              bgcolor: '#94a3b8', border: '3px solid #fff',
                              boxShadow: '0 0 0 2px #f1f5f9',
                            }} />
                            <Paper elevation={0} sx={{
                              p: 1.5, borderRadius: 2,
                              border: `1px solid ${BORDER}`, bgcolor: '#fff',
                              borderLeft: '4px solid #94a3b8',
                            }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                                      {d.date}
                                    </Typography>
                                    <Box sx={{
                                      px: 0.85, py: 0.25, borderRadius: 0.85,
                                      bgcolor: '#f1f5f9', color: '#475569',
                                      fontSize: '0.66rem', fontWeight: 700,
                                      textTransform: 'uppercase', letterSpacing: 0.4,
                                    }}>
                                      Direct entry
                                    </Box>
                                  </Stack>
                                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                                    <ArrowDownward sx={{ fontSize: 16, color: STATUS.approved.strong }} />
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: STATUS.approved.strong, fontVariantNumeric: 'tabular-nums' }}>
                                      +{parseInt(d.quantity || 0).toLocaleString()}
                                    </Typography>
                                  </Stack>
                                  <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                                    {d.code ? `Code: ${d.code} · ` : ''}Pricing: {d.unit || '—'}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          </Box>
                        );
                      });

                      return cards;
                    })()}
                  </Stack>
                </Box>
              </Box>

              {/* ───── RIGHT — Stock OUT ───── */}
              <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{
                  px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`,
                  bgcolor: '#fff',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.25,
                    bgcolor: 'rgba(14,165,233,0.10)', color: '#0369A1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <ArrowUpward sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={LABEL_SX}>Stock out</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }}>
                      Dispensed to patients
                    </Typography>
                  </Box>
                  <Box sx={{
                    px: 0.85, py: 0.25, borderRadius: 0.85,
                    bgcolor: 'rgba(14,165,233,0.10)', color: '#0369A1',
                    fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                  }}>
                    {(traceDialog.data?.outflows?.length || 0)} events
                  </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                  <Box sx={{
                    position: 'absolute', top: 16, bottom: 16, left: 30,
                    width: 2, bgcolor: 'rgba(14,165,233,0.15)', borderRadius: 1,
                  }} />
                  <Stack spacing={1.25} sx={{ p: 2 }}>
                    {(traceDialog.data?.outflows || []).length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <ArrowUpward sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                          No outflow events
                        </Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                          This product has not been dispensed to any patient in this range.
                        </Typography>
                      </Box>
                    ) : (
                      (traceDialog.data?.outflows || []).map((o) => {
                        const pharmacist = o.dispensedBy || '—';
                        const doctor     = o.prescribedBy || '—';
                        const colour     = colourFor(pharmacist);
                        const status     = String(o.status || '').toLowerCase();
                        const statusTone = status === 'paid'   ? { tint: 'rgba(16,185,129,0.10)', strong: '#15803D' }
                                         : status === 'credit' ? { tint: 'rgba(217,119,6,0.10)',  strong: '#92400E' }
                                         : status === 'cancelled' ? { tint: 'rgba(220,38,38,0.10)', strong: '#991B1B' }
                                         : { tint: '#f1f5f9', strong: '#475569' };
                        return (
                          <Box key={`out-${o.trans_code}`} sx={{ position: 'relative', pl: 4 }}>
                            <Box sx={{
                              position: 'absolute', left: 11, top: 14,
                              width: 14, height: 14, borderRadius: '50%',
                              bgcolor: '#0EA5E9', border: '3px solid #fff',
                              boxShadow: '0 0 0 2px rgba(14,165,233,0.18)',
                            }} />
                            <Paper elevation={0} sx={{
                              p: 1.5, borderRadius: 2,
                              border: `1px solid ${BORDER}`, bgcolor: '#fff',
                              borderLeft: '4px solid #0EA5E9',
                            }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                                      {String(o.date).slice(0, 10)}
                                    </Typography>
                                    {o.status && (
                                      <Box sx={{
                                        px: 0.85, py: 0.25, borderRadius: 0.85,
                                        bgcolor: statusTone.tint, color: statusTone.strong,
                                        fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                                      }}>
                                        {o.status}
                                      </Box>
                                    )}
                                    {o.receipt && (
                                      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                                        #{o.receipt}
                                      </Typography>
                                    )}
                                  </Stack>
                                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                                    <ArrowUpward sx={{ fontSize: 16, color: '#0369A1' }} />
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0369A1', fontVariantNumeric: 'tabular-nums' }}>
                                      −{parseInt(o.quantity || 0).toLocaleString()}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem', color: MUTED, fontWeight: 600 }}>
                                      unit{parseInt(o.quantity) === 1 ? '' : 's'}
                                    </Typography>
                                    {o.cost && Number(o.cost) > 0 && (
                                      <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontWeight: 600 }}>
                                        · GHS {Number(o.cost).toFixed(2)}
                                      </Typography>
                                    )}
                                  </Stack>

                                  {/* Patient → pharmacist trail */}
                                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                      <Avatar sx={{
                                        width: 22, height: 22,
                                        bgcolor: 'rgba(14,165,233,0.12)', color: '#0369A1',
                                        fontSize: '0.62rem', fontWeight: 800,
                                      }}>
                                        <PersonOutline sx={{ fontSize: 14 }} />
                                      </Avatar>
                                      <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                          Patient
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: STRONG, lineHeight: 1.1 }}>
                                          {o.patientName || '—'}
                                          {o.patientId && (
                                            <Typography component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: MUTED, fontWeight: 600 }}>
                                              ({o.patientId})
                                            </Typography>
                                          )}
                                        </Typography>
                                      </Box>
                                    </Stack>

                                    <Box sx={{ color: MUTED, mx: 0.5 }}>←</Box>

                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                      <Avatar sx={{
                                        width: 22, height: 22,
                                        bgcolor: colour.bg, color: colour.fg,
                                        fontSize: '0.62rem', fontWeight: 800,
                                      }}>
                                        <LocalPharmacy sx={{ fontSize: 14 }} />
                                      </Avatar>
                                      <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                          Dispensed by
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: STRONG, lineHeight: 1.1 }}>
                                          {pharmacist}
                                        </Typography>
                                      </Box>
                                    </Stack>

                                    {doctor && doctor !== '—' && (
                                      <Box sx={{
                                        px: 0.85, py: 0.25, borderRadius: 0.85,
                                        bgcolor: '#f1f5f9', color: '#475569',
                                        fontSize: '0.66rem', fontWeight: 700,
                                      }}>
                                        Rx: {doctor}
                                      </Box>
                                    )}
                                  </Stack>

                                  {(o.dose || o.frequency || o.route) && (
                                    <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.75 }}>
                                      {[o.dose && `Dose ${o.dose}${o.dose_unit ? ' ' + o.dose_unit : ''}`, o.frequency, o.route]
                                        .filter(Boolean).join(' · ')}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          </Box>
                        );
                      })
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: '0.74rem', color: MUTED, mr: 'auto', pl: 1.5 }}>
            Tip: filter by date or requester at the top to narrow the trail.
          </Typography>
          <Button
            onClick={handleCloseTrace}
            sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Requisition;
